<template>
  <ToggleGroupRoot
    class="ToggleButton"
    :class="[`ToggleButton--${size}`]"
    type="single"
    :modelValue="model"
    :disabled="disabled"
    :aria-label="ariaLabel"
    @update:model-value="handleValueChange"
  >
    <ToggleGroupItem
      v-for="option of options"
      :key="option.label"
      class="ToggleButton-option"
      :value="option.value"
      :aria-label="option.label || option.value"
    >
      <span class="ToggleButton-optionLabel" :data-text="option.label || option.value">
        {{ option.label || option.value }}
      </span>
    </ToggleGroupItem>
  </ToggleGroupRoot>
</template>

<script setup lang="ts">
import { type AcceptableValue, ToggleGroupItem, ToggleGroupRoot } from 'reka-ui';
import type { FieldProps } from '@cvo/ui/composables';
import type { ToggleButtonOption } from './ToggleButton.model';

export interface ToggleButtonProps extends FieldProps {
  /** Array of options to display */
  options: ToggleButtonOption[];
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Accessibility label for the group */
  ariaLabel?: string;
}

const props = withDefaults(defineProps<ToggleButtonProps>(), {
  size: 'md',
  disabled: false,
  ariaLabel: 'Toggle button group',
});

const model = defineModel<ToggleButtonOption['value'] | undefined>({
  default: undefined,
});

function handleValueChange(newValue: AcceptableValue | undefined) {
  // Ensure there's always a value selected (prevent deselection)
  if (newValue && !props.disabled) {
    model.value = newValue;
  }
};
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .ToggleButton {
    --toggle-border-radius: #{vars.$atoms-controls-togglebutton-container-border-radius};
    --toggle-padding: #{vars.$dimensions-d-2};
    --option-padding: #{vars.$dimensions-d-2};
    --option-font-size: #{vars.$font-size-xl};
    --option-letter-spacing: -0.72px;

    position: relative;
    display: inline-flex;
    background-color: vars.$atoms-controls-togglebutton-container-bg-color;
    border: vars.$atoms-controls-togglebutton-border-stroke solid vars.$atoms-controls-togglebutton-border-color;
    border-radius: var(--toggle-border-radius);
    padding: var(--toggle-padding);
    gap: vars.$dimensions-d-1;

    // Large size
    &--lg {
      --toggle-padding: #{vars.$dimensions-d-2};
      --option-padding: #{vars.$dimensions-d-2};
      --option-font-size: #{vars.$font-size-xl};
      --option-letter-spacing: -0.72px;
    }

    // Medium size
    &--md {
      --toggle-padding: #{vars.$dimensions-d-2};
      --option-padding: #{vars.$dimensions-d-2};
      --option-font-size: #{vars.$font-size-lg};
      --option-letter-spacing: -0.6px;
    }

    // Small size
    &--sm {
      --toggle-padding: #{vars.$dimensions-d-1};
      --option-padding: #{vars.$dimensions-d-1};
      --option-font-size: #{vars.$font-size-md};
      --option-letter-spacing: -0.48px;
    }

    // Disabled state
    &:has(.ToggleButton-option:disabled) {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .ToggleButton-option {
    position: relative;
    background-color: transparent;
    border: none;
    border-radius: var(--toggle-border-radius);
    cursor: pointer;
    transition: all vars.$transition-fast ease-in-out;
    color: vars.$atoms-controls-togglebutton-text-color;
    white-space: nowrap;
    outline: none;
    flex-shrink: 0;
    padding: var(--option-padding);
    font-size: var(--option-font-size);

    $active-label-weight: vars.$typography-weight-strong;

    // Default state
    .ToggleButton-optionLabel {
      display: block;
      line-height: 1;
      font-weight: vars.$typography-weight-body;
      letter-spacing: var(--option-letter-spacing);

      // Reserve space for the active label text. The "medium" font weight is used when active, and causes a layout shift if not reserved.
      &::before {
        content: attr(data-text);
        display: block;
        height: 0;
        overflow: hidden;
        visibility: hidden;
        font-weight: $active-label-weight;
      }
    }

    // Active state (using Reka's data attributes)
    &[data-state="on"] {
      background-color: vars.$atoms-controls-togglebutton-selected-bg-color;
      color: vars.$atoms-controls-togglebutton-selected-text-color;

      .ToggleButton-optionLabel {
        font-weight: $active-label-weight;
      }
    }

    // Hover state (only for inactive options)
    &:hover:not([data-state="on"]):not(:disabled) {
      background-color: vars.$color-neutral-gray-100;
    }

    // Focus state
    &:focus-visible {
      outline: 2px solid vars.$color-brand-primary-900;
      outline-offset: 2px;
    }

  // Disabled state
    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}
</style>
